import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faWallet } from "@fortawesome/free-solid-svg-icons";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import "../scss/index.scss";
import {
  apiRequestLimits,
  changeWallet,
  getActiveWallet,
  get2FAStatus,
} from "../store/actions";
import DataLoader from "../components/globalView/DataLoader";
import { Helmet } from "react-helmet";

const EditWallet = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const twoFactor = useSelector((state) => state.userReducer.twoFactor);

  const [wallet, setWallet] = useState("");
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [successMessage, setSuccessMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showUpdateDiv, setShowUpdateDiv] = useState(false);

  useEffect(() => {
    dispatch(getActiveWallet());
  }, [dispatch]);

  useEffect(() => {
    dispatch(get2FAStatus());
  }, [dispatch]);

  const showDiv = () => {
    setShowUpdateDiv(true);
  };
  const hideDiv = () => {
    setShowUpdateDiv(false);
  };
  const handleSubmit = async () => {
    const trimmed = (wallet || "").trim();
    if (!/^0x[a-fA-F0-9]{40}$/.test(trimmed)) {
      setErrorMessage(
        "Please enter a valid EVM wallet address (0x followed by 40 hex characters). Payouts are sent in USDC to this address."
      );
      return;
    }
    const code = (twoFactorCode || "").trim();
    if (!/^[0-9]{6}$/.test(code)) {
      setErrorMessage(
        "Enter the 6-digit code from your authenticator app to confirm this change."
      );
      return;
    }
    setErrorMessage("");
    setSubmitting(true);
    try {
      await dispatch(changeWallet(trimmed, code));
      setSuccessMessage(true);
      setTimeout(() => {
        setSuccessMessage(false);
        setShowUpdateDiv(false);
        setWallet("");
        setTwoFactorCode("");
      }, 1500);
    } catch (e) {
      setErrorMessage(e?.message || "Failed to update wallet. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };
  const isLoading = useSelector((state) => state.userReducer.loading);
  const activeWallet = useSelector((state) => state.userReducer.activeWallet);

  useEffect(() => {
    dispatch(apiRequestLimits());
  }, [dispatch]);

  return (
    <>
      <Helmet>
        <title>Wallet Management | Instaswap Partners</title>
      </Helmet>
      {isLoading ? (
        <DataLoader />
      ) : (
        <div>
          <div className="secHdr">
            <h2 className="secTitle">Edit Withdraw Wallet</h2>
            <p className="secLead">
              This is the wallet where your USDC payouts are sent. Make sure it
              is a correct EVM (0x…) address.
            </p>
          </div>

          <div className="acWrap">
            <div className="acCard" style={{ textAlign: "left" }}>
              <div className="wwIco">
                <FontAwesomeIcon icon={faWallet} />
              </div>

              {!showUpdateDiv && (
                <>
                  {activeWallet ? (
                    <>
                      <label className="fldLbl">Active withdraw wallet</label>
                      <div className="lwAddrBox" style={{ margin: "0 0 16px" }}>
                        {activeWallet}
                      </div>
                      <div className="notice" style={{ marginBottom: 16 }}>
                        <span className="ni">⚠</span>
                        <div>
                          <b>Notice:</b> please be careful — withdrawals are
                          always sent to the wallet specified above.
                        </div>
                      </div>
                    </>
                  ) : (
                    <p
                      style={{
                        color: "#a7b2ab",
                        textAlign: "center",
                        marginBottom: 16,
                      }}
                    >
                      You haven't set a withdraw wallet yet.
                    </p>
                  )}

                  <button
                    type="button"
                    className="btnPrimary block"
                    style={{ height: 50 }}
                    onClick={showDiv}
                  >
                    {activeWallet ? "Change wallet" : "Set up a withdraw wallet"}
                  </button>
                </>
              )}

              {showUpdateDiv && !twoFactor.loaded && (
                <div style={{ display: "flex", justifyContent: "center", padding: 20 }}>
                  <FontAwesomeIcon icon={faSpinner} spin color="#7c847e" />
                </div>
              )}

              {showUpdateDiv && twoFactor.loaded && !twoFactor.enabled && (
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  <div className="notice">
                    <span className="ni">⚠</span>
                    <div>
                      You must enable two-factor authentication before you can
                      change your withdrawal wallet.
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btnPrimary"
                    onClick={() => navigate("/security")}
                  >
                    Set up 2FA
                  </button>
                </div>
              )}

              {showUpdateDiv && twoFactor.loaded && twoFactor.enabled && (
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  <p style={{ color: "#a7b2ab", fontSize: 13, lineHeight: 1.55, margin: 0 }}>
                    Please double-check the wallet address. Funds will be lost
                    if the withdrawal is sent to the wrong address.
                  </p>
                  <div>
                    <label className="fldLbl">Wallet address</label>
                    <input
                      className="input mono"
                      type="text"
                      name="wallet"
                      placeholder="0x…"
                      value={wallet}
                      required
                      onChange={(e) => setWallet(e.target.value)}
                    />
                    <div className="fldHelp">
                      Use an EVM (Ethereum) address starting with 0x. Payouts
                      are sent in USDC on the Ethereum network. Do not use a
                      Bitcoin or exchange-memo address.
                    </div>
                  </div>
                  <div>
                    <label className="fldLbl">Two-factor code</label>
                    <input
                      className="input mono"
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      name="twoFactorCode"
                      placeholder="123456"
                      value={twoFactorCode}
                      required
                      onChange={(e) =>
                        setTwoFactorCode(e.target.value.replace(/\D/g, ""))
                      }
                    />
                    <div className="fldHelp">
                      Enter the 6-digit code from your authenticator app to
                      confirm the withdrawal wallet change.
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 12 }}>
                    <button
                      type="button"
                      className="btnPrimary"
                      style={{ flex: 1 }}
                      onClick={handleSubmit}
                      disabled={submitting}
                    >
                      {submitting ? (
                        <FontAwesomeIcon icon={faSpinner} spin />
                      ) : activeWallet ? (
                        "Update wallet"
                      ) : (
                        "Save wallet"
                      )}
                    </button>
                    <button
                      type="button"
                      className="btnGhost"
                      style={{ flex: 1 }}
                      onClick={hideDiv}
                    >
                      Cancel
                    </button>
                  </div>
                  {errorMessage && (
                    <div className="notice red">
                      <span className="ni">⚠</span>
                      <div>{errorMessage}</div>
                    </div>
                  )}
                  {successMessage && (
                    <div className="notice green">
                      <span className="ni">✓</span>
                      <div>Your wallet was saved successfully</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EditWallet;
